// Where the open document sits in its lineage (#11956, #13681): the content
// states newest first, how far back each is from the latest, and whether a
// state's place is only a suggestion. The Versions section in Details and the
// older-version notice above the viewer both read it, so the two never
// disagree about what a document is.
import type {
  DocumentLineage,
  LineageMember,
} from "./services/useDocumentMetadata";

// Content states are the spine of the list and copies its leaves: a rail
// runs down the head of each state, newest first, and the state's other
// members branch off it as copies. A state's head is usually its revision,
// but the member the backend names latest heads its state even when it is a
// copy (the detector's latest is in a folder the viewer cannot open), so one
// row reads Latest. States are never numbered here, since the documents
// carry version numbers of their own that would clash: the newest reads
// Latest, the one before it Previous revision, anything older "N revisions
// earlier", counted back from the latest.
export interface VersionBlock {
  // 0 for the newest content state, counting back over the states a fact
  // attached; a suggestion-only state is not counted.
  back: number;
  // A state only text similarity attached that is newer than the latest: a
  // suggestion never outranks a fact, so it is not the latest, and it is not a
  // step back either. It reads as a possible revision with its likelihood.
  suggested: boolean;
  head: LineageMember;
  copies: LineageMember[];
}

// Newest content state first, each head with its copies by ordinal. Content
// states are ordered by the members' ordinals, which the lineage assigns
// oldest state first; the state the lineage marks latest leads whatever
// its ordinal, so a pinned latest reads Latest.
export function versionBlocks(lineage: DocumentLineage): VersionBlock[] {
  const byState = new Map<string, LineageMember[]>();
  for (const member of lineage.members) {
    const group = byState.get(member.content_group);
    if (group) group.push(member);
    else byState.set(member.content_group, [member]);
  }
  const states = [...byState.values()].map((members) => {
    const sorted = [...members].sort((a, b) => a.ordinal - b.ordinal);
    // The member the backend names latest heads its state, so the one row
    // reading Latest is that member; the backend falls back to the newest
    // member the viewer can open when the detector's latest is hidden, which
    // may be a copy. Otherwise the state's revision heads it, else its first
    // member (a state whose revision the viewer cannot open).
    const head =
      sorted.find((m) => m.is_latest) ??
      sorted.find((m) => m.relation === "revision") ??
      sorted[0];
    const latest = sorted.some((m) => m.is_latest);
    return { head, copies: sorted.filter((m) => m !== head), latest };
  });
  states.sort((a, b) =>
    a.latest !== b.latest
      ? Number(b.latest) - Number(a.latest)
      : b.head.ordinal - a.head.ordinal
  );
  const latestOrdinal = states.find((st) => st.latest)?.head.ordinal;
  let back = 0;
  return states.map(({ head, copies, latest }) => {
    const suggested =
      !latest &&
      latestOrdinal !== undefined &&
      head.ordinal > latestOrdinal &&
      [head, ...copies].every((m) => m.confidence === "suggested");
    const block = { back, suggested, head, copies };
    if (!suggested) back += 1;
    return block;
  });
}

// What a copy in another format is called: the format, not the storage
// kind's enum spelling.
const KIND_LABELS: Record<string, string> = {
  pdf: "PDF",
  docx: "Word",
  doc: "Word",
  pptx: "PowerPoint",
  ppt: "PowerPoint",
  excel: "Excel",
  xlsx: "Excel",
  xls: "Excel",
  csv: "CSV",
  txt: "text",
  email: "email",
  "onenote-page": "OneNote",
};

function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind.replace(/[-_]+/g, " ");
}

export function isCopyOf(member: LineageMember, block: VersionBlock): boolean {
  return member.blob_id !== block.head.blob_id;
}

// The note after a member linked by text similarity: how likely it is to be
// one document with the rest, as a percent. Rounded, and never 100: an
// inference never reads as certain. A member written before the score
// carries no number and keeps the word, so a suggestion never reads as a
// fact.
export function likelyLabel(probability: number): string {
  return `${Math.min(99, Math.round(probability * 100))}% likely`;
}

// The note a member's row carries after its status: the likelihood for a
// member linked by text similarity, the word for one written before the
// score, nothing for a fact. Details and the documents grid's Versions
// column (#13682) both show it.
export function noteOf(member: LineageMember): string | null {
  if (member.confidence !== "suggested") return null;
  return member.probability != null
    ? likelyLabel(member.probability)
    : "Suggested";
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

// "2 revisions, 1 copy": the content states before the latest, and the
// members that repeat one of the states. The latest state is not counted as
// a revision, since a revision is a change to something and it is the thing
// the rows count back from: a document with one duplicate reads "1 copy",
// not "1 revision, 1 copy". Details' Versions section and the documents
// grid's Versions band (#13682) both read it, for lineages of two or more
// members, so at least one of the counts is always present.
export function summaryOf(lineage: DocumentLineage): string {
  const states = new Set(lineage.members.map((m) => m.content_group)).size;
  const revisions = Math.max(states - 1, 0);
  const copies = Math.max(lineage.member_count - states, 0);
  const parts: string[] = [];
  if (revisions > 0) parts.push(plural(revisions, "revision", "revisions"));
  if (copies > 0) parts.push(plural(copies, "copy", "copies"));
  return parts.join(", ");
}

// A content state's place counted back from the latest, never a version
// number of its own: the document may carry one, and "2 revisions earlier"
// cannot clash with it.
export function revisionLabel(back: number): string {
  if (back === 0) return "Latest";
  if (back === 1) return "Previous revision";
  return `${back} revisions earlier`;
}

export function statusOf(member: LineageMember, block: VersionBlock): string {
  if (isCopyOf(member, block)) {
    // A copy in another format says so; a copy in the same format is just a
    // copy, and its filename and folder are on the card.
    return member.blob_kind !== block.head.blob_kind
      ? `Copy (${kindLabel(member.blob_kind)})`
      : "Copy";
  }
  if (block.suggested) return "Possible revision";
  return revisionLabel(block.back);
}

// The open document's state: current when it is the latest's content (the
// latest, or a copy of it in any format), when it is in no lineage, or when
// no member is marked latest (the viewer could not open one); otherwise
// earlier, with how far back it is, whether that is only a suggestion, and
// the latest to open instead. The same predicate as the backend's
// `carries_current_content`.
export type CurrentVersionStatus =
  | { kind: "current" }
  | {
      kind: "earlier";
      back: number;
      suggested: boolean;
      latest: LineageMember;
    };

export function currentVersionStatus(
  lineage: DocumentLineage | null | undefined
): CurrentVersionStatus {
  if (!lineage) return { kind: "current" };
  const latest = lineage.members.find((m) => m.is_latest);
  const open = lineage.members.find((m) => m.is_current);
  if (!latest || !open || open.content_group === latest.content_group) {
    return { kind: "current" };
  }
  // versionBlocks makes a block for every content_group, the open
  // document's included, so the find always lands.
  const block = versionBlocks(lineage).find(
    (b) => b.head.content_group === open.content_group
  )!;
  const openSuggested = open.confidence === "suggested";
  const latestSuggested = latest.confidence === "suggested";
  return {
    kind: "earlier",
    back: block.back,
    suggested: block.suggested || openSuggested || latestSuggested,
    latest,
  };
}
